import React, { useState } from 'react';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useRecaptcha } from '../hooks/useRecaptcha';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';

const LoginPage = () => {

  const { executeReCaptcha, cleanupReCaptcha } = useRecaptcha();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });

  const [error, setError] = useState('');
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { login } = useAuth();
  const formRef = React.useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmiting(true);

    executeReCaptcha('login', async (recaptchaToken) => {
      if (!recaptchaToken) {
        setError('Unable to verify you. Please try again.');
        setIsSubmiting(false);
        return;
      }

      try {
        const res = await API.post('/api/auth/login', {
          ...formData,
          recaptchaToken, // Add token to request body
        });

        const userData = {
          role: res.data.role,
          username: res.data.username,
          rememberMe: formData.rememberMe,
        }
        if (res.data.database) {
          userData.database = {
            id: res.data.database.id,
            name: res.data.database.name
          }
        }
        login(res.data.token, userData);
        cleanupReCaptcha();
        setError('');
      } catch (err) {
        setError(err.response?.data?.msg || 'Login failed');
      } finally {
        setIsSubmiting(false);
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <Box sx={{ width: '300px', p: 4, borderRadius: 4, backgroundColor: 'background.paper' }}>
        <Typography variant="h4" gutterBottom>
          Top Boxing
        </Typography>
        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleKeyPress}>
          <TextField
            margin="normal"
            fullWidth
            label="Username"
            value={formData.username}
            onChange={(e) =>
              setFormData({ ...formData, username: e.target.value })
            }
            required
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            required
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.rememberMe}
                onChange={(e) =>
                  setFormData({ ...formData, rememberMe: e.target.checked })
                }
              />
            }
            label="Remember Me"
            sx={{ mb: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mb: 2 }}
          >
            {isSubmiting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Login'
            )}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default LoginPage;
